import React from 'react';

import { FileBox, MCButton } from 'src/components/basic';
import BaseComponent from 'src/components/BaseComponent';
import config from 'src/config';

import { humanFileSize, lang, remCss } from 'src/helpers';
import model from './model';
import TextareaAutosize from 'react-textarea-autosize';
import { getFileBoxType } from 'components/basic/FileBox/utils';
import SelfDestruct from 'components/messages/chat/SelfDestruct';
import LANG_CONST from 'src/helpers/lang/const';
import { isAllowedFile, isValidFile } from 'src/helpers/fileCheck';

const maxFileSize = config.get('maxFileSize');
const maxFileCount = config.get('maxFileCount');

const FilesMemorized = React.memo(({ onCancelImage, files }) => {
  const multi = files.length > 1;
  return (
    <div className={'mc-flex-20-col'}>
      {files.map((f, index) => (
        <FileBox
          videoProps={{ controls: false, playsinline: true }}
          styles={{ width: remCss(60), height: remCss(60) }}
          onlyPreview={true}
          showFileName={true}
          showFileSize={true}
          list={files}
          template={2}
          fileId={index}
          key={'mc-media-' + index}
          file={files[index]}
          onClose={!multi ? null : () => onCancelImage(index)}
        />
      ))}
    </div>
  );
});

class ChatFileInput extends BaseComponent {
  constructor(props) {
    super(props);

    let files = props.files || [];
    let error = null;

    // Validate files from props
    if (maxFileCount > 0 && files.length > maxFileCount) {
      error = lang.t(`You can only upload up to ${maxFileCount} files`);
      files = [];
    }

    if (maxFileSize > 0 && !error) {
      const oversizedFile = files.find((file) => file.size > maxFileSize);

      if (oversizedFile) {
        error = lang.t(LANG_CONST.validation.file_too_big, {
          size: humanFileSize(maxFileSize)
        });
        files = [];
      }
    }

    this.state = {
      files: files,
      text: props.text,
      error: error
    };
  }

  componentDidMount() {
    model.subscribe(this.subscription);
    setTimeout(() => {
      this.textInputRef?.focus();
      this.textInputRef.value = '';
      this.textInputRef.value = this.state.text;
    }, 1);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  submit = (e) => {
    e.preventDefault();
    const { files } = this.state;
    const { hide } = this.props;
    model.listHandleFiles('files', files);
    model.createMessage();
    hide();
  };
  onCancelImage = (index) => {
    this.setState({ files: this.state.files.filter((f, i) => i !== index) });
  };

  onUpload = async (e) => {
    const newFiles = Object.values(e.target.files);
    const currentFiles = this.state.files;
    const allowedFiles = newFiles.filter(isAllowedFile);
    const contentOk = await Promise.all(allowedFiles.map(isValidFile));
    const filteredFiles = allowedFiles.filter((file, index) => contentOk[index]);

    if (newFiles.length > filteredFiles.length) {
      this.setState({
        error: lang.t(LANG_CONST.validation.file_invalid)
      });
      e.target.value = '';
      return;
    }

    if (maxFileCount > 0 && currentFiles.length + filteredFiles.length > maxFileCount) {
      this.setState({
        error: lang.t(LANG_CONST.validation.media_limit, { limit: maxFileCount })
      });
      e.target.value = '';
      return;
    }

    if (maxFileSize > 0) {
      const oversizedFile = filteredFiles.find((file) => file.size > maxFileSize);
      if (oversizedFile) {
        this.setState({
          error: lang.t(LANG_CONST.validation.file_too_big, {
            size: humanFileSize(maxFileSize)
          })
        });
        e.target.value = '';
        return;
      }
    }

    this.setState({ files: [...currentFiles, ...filteredFiles], error: null });
    e.target.value = '';
  };

  renderImages = () => {
    const { files } = this.state;
    const multi = files.length > 1;

    return (
      <div className={'mc-flex-5-col'}>
        <div className={'mc-flex-5 mc-flex-start'} style={{ height: remCss(180) }}>
          <div className={'mc-grow'}>
            <FileBox
              list={files}
              key={'mc-media-' + 0}
              file={files[0]}
              onClose={!multi ? null : () => this.onCancelImage(0)}
              styles={{ width: '100%', height: remCss(180) }}
              onlyPreview={true}
              showFileName={false}
            />
          </div>
          {multi && (
            <div className={'mc-flex-5 mc-grow mc-wrap'}>
              {files
                .slice(0, 5)
                .map(
                  (f, index) =>
                    index !== 0 && (
                      <FileBox
                        list={files}
                        key={'mc-media-' + index}
                        file={files[index]}
                        onClose={() => this.onCancelImage(index)}
                        styles={{ width: remCss(85), height: remCss(85) }}
                        onlyPreview={true}
                        showFileName={false}
                      />
                    )
                )}
            </div>
          )}
        </div>
        <div className={'mc-flex-5 mc-grow mc-wrap'}>
          {files.slice(5).map((f, index) => (
            <FileBox
              list={files}
              key={'mc-media-' + index}
              file={files[5 + index]}
              onClose={() => this.onCancelImage(5 + index)}
              styles={{ width: remCss(85), height: remCss(85) }}
              onlyPreview={true}
              showFileName={false}
            />
          ))}
        </div>
      </div>
    );
  };

  addNewLineToTextArea = () => {
    const { text = '' } = this.state;
    let newValue = text + '\r\n';
    this.setState({ text: newValue });
  };

  onKeyPressInput = (event) => {
    if (event.key === 'Enter') {
      this.addNewLineToTextArea();
    }
  };
  onChangeInput = (event) => {
    console.log('ChatFileInput onChangeInput');
    const value = event.target.value;
    model.onChangeInput('input', value);
    this.setState({ text: value });
  };

  render() {
    const { files, text, error } = this.state;
    const { closeBtn, hide } = this.props;
    const fileType = files.find((f) => getFileBoxType(f) !== 'image') ? 'file' : 'photo';
    let title = 'Send ' + (files.length === 1 ? fileType : files.length + ' ' + fileType + 's');
    return (
      <div className={'mc-popup-contain'} style={{ width: remCss(400) }}>
        <div className='mc-modal-header'>
          <div className='mc-popup-title'>{lang.t(title)}</div>
          {closeBtn}
        </div>
        <div className='mc-modal-body mc-flex-15-col'>
          {error && (
            <div
              className='mc-error-message'
              style={{
                padding: remCss(10),
                backgroundColor: '#fee',
                color: '#c00',
                borderRadius: remCss(4),
                margin: `${remCss(10)}px ${remCss(15)}px`,
                fontSize: remCss(14)
              }}
            >
              {error}
            </div>
          )}
          {fileType === 'photo' ? (
            this.renderImages()
          ) : (
            <FilesMemorized files={files} onCancelImage={this.onCancelImage} />
          )}

          <SelfDestruct
            onChange={(v) => {
              model.listHandleChange('selfDestruct', v);
            }}
          />
          <div className={'mc-send-box-msg-input'}>
            <TextareaAutosize
              autoFocus
              ref={(ref) => {
                this.textInputRef = ref;
              }}
              className='mc-chat-input'
              type='text'
              placeholder={lang.t('Add a caption...')}
              onKeyUp={this.onKeyPressInput}
              onChange={this.onChangeInput}
              value={text}
              maxRows={5}
              minRows={1}
              style={{ resize: 'none' }}
            />
          </div>
        </div>
        <div className='mc-modal-footer mc-flex'>
          <MCButton small primary light radius type={'button'} onClick={() => this.file?.click()}>
            {lang.t('Add')}
          </MCButton>
          <input
            type={'file'}
            multiple={true}
            ref={(r) => (this.file = r)}
            onChange={this.onUpload}
            hidden
          />
          <div className={'mc-grow'} />
          <MCButton small primary light radius type={'button'} onClick={hide}>
            {lang.t('Cancel')}
          </MCButton>
          <MCButton small primary dark radius type={'button'} onClick={this.submit}>
            {lang.t('Send')}
          </MCButton>
        </div>
      </div>
    );
  }
}

export default ChatFileInput;
